// Email validation utilities for ZoningPal authentication
// Implements professional-grade email validation

export interface EmailValidationResult {
  isValid: boolean;
  error: string | null;
  suggestion: string | null;
}

// Common email domain typos and their corrections
const DOMAIN_CORRECTIONS: Record<string, string> = {
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'yahoo.co': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmil.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'iclou.com': 'icloud.com',
  'iclood.com': 'icloud.com',
};

// Professional email domains (not exhaustive, but common ones)
const PROFESSIONAL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'protonmail.com', 'hey.com', 'fastmail.com', 'zoho.com'
];

// Disposable/temporary email domains to warn about
const DISPOSABLE_DOMAINS = [
  '10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com',
  'temp-mail.org', 'throwaway.email', 'yopmail.com', 'maildrop.cc'
];

// Basic email format validation using RFC 5322 compliant regex (simplified)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Validate email format
export const validateEmailFormat = (email: string): { isValid: boolean; error: string | null } => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  if (email.length > 254) {
    return { isValid: false, error: 'Email is too long (max 254 characters)' };
  }

  if (!EMAIL_REGEX.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Check for common issues
  if (email.includes('..')) {
    return { isValid: false, error: 'Email cannot contain consecutive dots' };
  }

  if (email.startsWith('.') || email.endsWith('.')) {
    return { isValid: false, error: 'Email cannot start or end with a dot' };
  }

  const [localPart, domain] = email.split('@');
  
  if (localPart.length > 64) {
    return { isValid: false, error: 'Email username part is too long' };
  }

  if (domain.length > 253) {
    return { isValid: false, error: 'Email domain is too long' };
  }

  return { isValid: true, error: null };
};

// Check for domain typos and suggest corrections
export const suggestEmailCorrection = (email: string): string | null => {
  if (!email.includes('@')) return null;

  const [localPart, domain] = email.split('@');
  const lowerDomain = domain.toLowerCase();

  if (DOMAIN_CORRECTIONS[lowerDomain]) {
    return `${localPart}@${DOMAIN_CORRECTIONS[lowerDomain]}`;
  }

  return null;
};

// Check if email uses a disposable domain
export const isDisposableEmail = (email: string): boolean => {
  if (!email.includes('@')) return false;
  
  const domain = email.split('@')[1].toLowerCase();
  return DISPOSABLE_DOMAINS.includes(domain);
};

// Check if email uses a professional domain
export const isProfessionalEmail = (email: string): boolean => {
  if (!email.includes('@')) return false;
  
  const domain = email.split('@')[1].toLowerCase();
  return PROFESSIONAL_DOMAINS.includes(domain) || 
         (!domain.includes('.com') && domain.includes('.')); // Likely custom domain
};

// Main email validation function
export const validateEmail = async (email: string): Promise<EmailValidationResult> => {
  // Basic format validation
  const formatValidation = validateEmailFormat(email);
  if (!formatValidation.isValid) {
    return {
      isValid: false,
      error: formatValidation.error,
      suggestion: null
    };
  }

  // Check for typos and suggest corrections
  const suggestion = suggestEmailCorrection(email);
  
  // Warn about disposable emails
  if (isDisposableEmail(email)) {
    return {
      isValid: false,
      error: 'Please use a permanent email address (temporary email detected)',
      suggestion: null
    };
  }

  return {
    isValid: true,
    error: null,
    suggestion
  };
};

// Quick email format validation for real-time feedback
export const isEmailValid = (email: string): boolean => {
  if (!email) return false;
  return validateEmailFormat(email).isValid && !isDisposableEmail(email);
};

// Get user-friendly error message for form display
export const getEmailError = (email: string): string | null => {
  if (!email) return null; // Don't show error for empty email during typing
  
  const validation = validateEmailFormat(email);
  if (!validation.isValid) return validation.error;
  
  if (isDisposableEmail(email)) {
    return 'Please use a permanent email address';
  }
  
  return null;
};

// Format email for consistent storage (lowercase, trimmed)
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
}; 